import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useState } from "react";
import { format } from "date-fns";
import {
  ArrowLeft,
  Save,
  Power,
  PowerOff,
  ExternalLink,
} from "lucide-react";
import {
  adminApi,
  type AdminSchoolDetail,
  type UpdateSchoolPatch,
} from "@/lib/api";
import { queryKeys } from "@/lib/queries";
import { useDirtyGuardedSeed } from "@/hooks/useDirtyGuardedSeed";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { StatusPill } from "@/components/StatusPill";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authed/schools_/$id")({
  component: SchoolDetailPage,
});

interface FormState {
  name: string;
  urn: string;
  contactPerson: string;
  contactEmail: string;
  contactPhone: string;
  street: string;
  address: string;
  postcode: string;
  website: string;
  logoUrl: string;
  acceptsDropoffs: boolean;
  dropoffInstructions: string;
}

function fromDetail(d: AdminSchoolDetail): FormState {
  return {
    name: d.name,
    urn: d.urn ?? "",
    contactPerson: d.contact?.person ?? "",
    contactEmail: d.contact?.email ?? "",
    contactPhone: d.contact?.phone ?? "",
    street: d.location?.street ?? "",
    address: d.location?.address ?? "",
    postcode: d.location?.postcode ?? "",
    website: d.website ?? "",
    logoUrl: d.logoUrl ?? "",
    acceptsDropoffs: d.acceptsDropoffs ?? false,
    dropoffInstructions: d.dropoffInstructions ?? "",
  };
}

function diffToPatch(initial: FormState, current: FormState): UpdateSchoolPatch {
  const patch: UpdateSchoolPatch = {};
  if (current.name !== initial.name) patch.name = current.name;
  if (current.urn !== initial.urn) {
    patch.urn = current.urn.trim() === "" ? null : current.urn.trim();
  }
  const contactChanged =
    current.contactPerson !== initial.contactPerson ||
    current.contactEmail !== initial.contactEmail ||
    current.contactPhone !== initial.contactPhone;
  if (contactChanged) {
    patch.contact = {
      person: current.contactPerson || undefined,
      email: current.contactEmail || undefined,
      phone: current.contactPhone || undefined,
    };
  }
  const locationChanged =
    current.street !== initial.street ||
    current.address !== initial.address ||
    current.postcode !== initial.postcode;
  if (locationChanged) {
    patch.location = {
      street: current.street || undefined,
      address: current.address || undefined,
      postcode: current.postcode || undefined,
    };
  }
  if (current.website !== initial.website) {
    patch.website = current.website.trim() === "" ? null : current.website.trim();
  }
  if (current.logoUrl !== initial.logoUrl) {
    patch.logoUrl = current.logoUrl.trim() === "" ? null : current.logoUrl.trim();
  }
  if (current.acceptsDropoffs !== initial.acceptsDropoffs) {
    patch.acceptsDropoffs = current.acceptsDropoffs;
  }
  if (current.dropoffInstructions !== initial.dropoffInstructions) {
    patch.dropoffInstructions =
      current.dropoffInstructions.trim() === "" ? null : current.dropoffInstructions.trim();
  }
  return patch;
}

function SchoolDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const [initial, setInitial] = useState<FormState | null>(null);
  const [actionMessage, setActionMessage] = useState<{
    tone: "success" | "danger";
    text: string;
  } | null>(null);

  const detail = useQuery({
    queryKey: queryKeys.schools.detail(id),
    queryFn: async () => (await adminApi.getSchool()({ id })).data,
  });

  const dirtyPatch = form && initial ? diffToPatch(initial, form) : {};
  const isDirty = Object.keys(dirtyPatch).length > 0;

  useDirtyGuardedSeed(detail.data, isDirty, fromDetail, setForm, setInitial);

  function flash(tone: "success" | "danger", text: string) {
    setActionMessage({ tone, text });
    window.setTimeout(() => setActionMessage(null), 5000);
  }

  function refresh() {
    void qc.invalidateQueries({ queryKey: queryKeys.schools.detail(id) });
    void qc.invalidateQueries({ queryKey: queryKeys.schools.all });
  }

  const update = useMutation({
    mutationFn: async ({ patch }: { patch: UpdateSchoolPatch; form: FormState }) =>
      (await adminApi.updateSchool()({ id, patch })).data,
    onSuccess: (_data, variables) => {
      flash("success", "Saved.");
      // Sync the pristine snapshot to the form that was actually submitted
      // (via mutation variables, not a closed-over `form`) so the form goes
      // pristine immediately and a subsequent refetch can re-seed normally.
      setInitial(variables.form);
      refresh();
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  const setActive = useMutation({
    mutationFn: async (isActive: boolean) =>
      (await adminApi.setSchoolActive()({ id, isActive })).data,
    onSuccess: ({ isActive }) => {
      flash("success", isActive ? "Reactivated." : "Deactivated.");
      refresh();
    },
    onError: (e: Error) => flash("danger", e.message),
  });

  function onSave() {
    if (!isDirty || !form) return;
    update.mutate({ patch: dirtyPatch, form });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/schools" })}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Schools
        </Button>
        <span className="text-muted-foreground">/</span>
        <span className="font-mono text-xs text-muted-foreground">{id}</span>
      </div>

      {detail.isLoading ? (
        <Card>
          <CardContent className="p-8 text-sm text-muted-foreground">Loading…</CardContent>
        </Card>
      ) : detail.isError ? (
        <Card>
          <CardContent className="p-8 text-sm text-destructive">
            Failed to load school: {(detail.error as Error).message}
          </CardContent>
        </Card>
      ) : detail.data && form && initial ? (
        <>
          <SchoolHeader detail={detail.data} />

          {actionMessage ? (
            <div
              className={cn(
                "rounded-md border p-3 text-sm",
                actionMessage.tone === "success"
                  ? "border-success/40 bg-success/5 text-success"
                  : "border-destructive/40 bg-destructive/5 text-destructive",
              )}
            >
              {actionMessage.text}
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardContent className="space-y-3 p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Identity
                </h2>
                <Field label="Name">
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </Field>
                <Field label="URN (DfE)">
                  <Input
                    value={form.urn}
                    onChange={(e) => setForm({ ...form, urn: e.target.value })}
                    placeholder="e.g. 100001"
                  />
                </Field>
                <Field label="Website">
                  <Input
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    placeholder="example.org.uk"
                  />
                </Field>
                <Field label="Logo URL">
                  <Input
                    value={form.logoUrl}
                    onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
                    placeholder="/logos/example.png"
                  />
                </Field>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Contact
                </h2>
                <Field label="Person">
                  <Input
                    value={form.contactPerson}
                    onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
                  />
                </Field>
                <Field label="Email">
                  <Input
                    type="email"
                    value={form.contactEmail}
                    onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    value={form.contactPhone}
                    onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                  />
                </Field>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardContent className="grid gap-3 p-5 md:grid-cols-3">
                <h2 className="col-span-full text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Location
                </h2>
                <Field label="Street">
                  <Input
                    value={form.street}
                    onChange={(e) => setForm({ ...form, street: e.target.value })}
                  />
                </Field>
                <Field label="Address">
                  <Input
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                  />
                </Field>
                <Field label="Postcode">
                  <Input
                    value={form.postcode}
                    onChange={(e) =>
                      setForm({ ...form, postcode: e.target.value.toUpperCase() })
                    }
                  />
                </Field>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardContent className="space-y-3 p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  School drop-offs
                </h2>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.acceptsDropoffs}
                    onChange={(e) => setForm({ ...form, acceptsDropoffs: e.target.checked })}
                  />
                  Accepts drop-offs (opt-in — finders can leave items at the office)
                </label>
                <Field label="Drop-off instructions (shown to the finder)">
                  <Input
                    value={form.dropoffInstructions}
                    onChange={(e) => setForm({ ...form, dropoffInstructions: e.target.value })}
                    placeholder="e.g. Reception, term time 08:00–16:00"
                    maxLength={500}
                  />
                </Field>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardContent className="space-y-3 p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Actions
                </h2>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={onSave}
                    disabled={!isDirty || update.isPending}
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {update.isPending ? "Saving…" : isDirty ? "Save changes" : "No changes"}
                  </Button>
                  {detail.data.status.isActive ? (
                    <Button
                      variant="destructive"
                      onClick={() => {
                        if (window.confirm("Deactivate this school? Users will no longer be able to pick it.")) {
                          setActive.mutate(false);
                        }
                      }}
                      disabled={setActive.isPending}
                    >
                      <PowerOff className="mr-2 h-4 w-4" />
                      Deactivate
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setActive.mutate(true)}
                      disabled={setActive.isPending}
                    >
                      <Power className="mr-2 h-4 w-4" />
                      Reactivate
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => {
                      setForm(initial);
                      flash("success", "Reverted unsaved edits.");
                    }}
                    disabled={!isDirty || update.isPending}
                  >
                    Discard changes
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Edits and active-state toggles are audit-logged. Hard delete is not exposed —
                  deactivation hides the school from end-user pickers while preserving the doc.
                </p>
              </CardContent>
            </Card>
          </div>
        </>
      ) : null}
    </div>
  );
}

function SchoolHeader({ detail }: { detail: AdminSchoolDetail }) {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-start justify-between gap-4 p-5">
        <div className="space-y-1">
          <div className="text-xs font-mono text-muted-foreground">{detail.id}</div>
          <h1 className="text-xl font-semibold tracking-tight">{detail.name}</h1>
          <div className="flex flex-wrap items-center gap-2">
            {detail.status.isActive ? (
              <StatusPill tone="success">active</StatusPill>
            ) : (
              <StatusPill tone="neutral">inactive</StatusPill>
            )}
            {detail.urn ? (
              <span className="font-mono text-xs text-muted-foreground">URN {detail.urn}</span>
            ) : null}
            {detail.metadata.source ? (
              <StatusPill tone="info">{detail.metadata.source}</StatusPill>
            ) : null}
            {detail.acceptsDropoffs ? <StatusPill tone="info">drop-offs</StatusPill> : null}
            {detail.website ? (
              <a
                href={
                  detail.website.startsWith("http")
                    ? detail.website
                    : `https://${detail.website}`
                }
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                {detail.website.replace(/^https?:\/\//, "")}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : null}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-right text-xs">
          <span className="text-muted-foreground">Labels</span>
          <span className="font-mono">{detail.status.labelCount.toLocaleString()}</span>
          <span className="text-muted-foreground">Users registered</span>
          <span className="font-mono">{detail.registeredUsers.toLocaleString()}</span>
          <span className="text-muted-foreground">Created</span>
          <span className="font-mono">
            {detail.metadata.createdAt
              ? format(new Date(detail.metadata.createdAt), "yyyy-MM-dd")
              : "—"}
          </span>
          <span className="text-muted-foreground">Updated</span>
          <span className="font-mono">
            {detail.metadata.updatedAt
              ? format(new Date(detail.metadata.updatedAt), "yyyy-MM-dd")
              : "—"}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
